import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import { useAuth } from "../../hooks/useAuth";
import { DEFAULT_ACCENT } from "../../hooks/useReaderSettings";

interface Highlight {
  id: string;
  bookId: number;
  bookName: string;
  chapter: number;
  verseNum: number;
  verseText: string;
  color: string;
  translation: string;
  createdAt: string;
  userId: string;
}

const STORAGE_KEY = "applesmp_highlights";

function getAllHighlights(): Highlight[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]");
  } catch {
    return [];
  }
}

function saveAllHighlights(data: Highlight[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export default function FavoritesPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const accentColor = localStorage.getItem("applecore_accent_color") ?? DEFAULT_ACCENT;

  const fetchHighlights = useCallback(() => {
    if (!user) return;
    const data = getAllHighlights().filter((h) => h.userId === user.id);
    setHighlights(data);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    fetchHighlights();
  }, [user, authLoading, navigate, fetchHighlights]);

  function handleDelete(id: string) {
    const all = getAllHighlights().filter((h) => h.id !== id);
    saveAllHighlights(all);
    setHighlights((prev) => prev.filter((h) => h.id !== id));
  }

  const books = [...new Set(highlights.map((h) => h.bookName))];
  const filtered = filter === "all" ? highlights : highlights.filter((h) => h.bookName === filter);

  const grouped: Record<string, Highlight[]> = {};
  filtered.forEach((h) => {
    const key = `${h.bookName} ${h.chapter}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(h);
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 md:px-6 pt-24 pb-20">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Highlighted Verses</h1>
          <p className="text-[#555] text-sm">
            Your personally highlighted scripture passages, saved on this device.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div
              className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: `${accentColor} transparent transparent transparent` }}
            />
          </div>
        ) : highlights.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-[#111] border border-[#1e1e1e] rounded-2xl">
              <i className="ri-bookmark-line text-2xl text-[#444]" />
            </div>
            <h3 className="text-white text-lg font-semibold">No highlights yet</h3>
            <p className="text-[#555] text-sm max-w-sm">
              Head to the Bible Reader and tap any verse to highlight it. It&apos;ll show up here.
            </p>
            <Link
              to="/scripture"
              className="mt-2 flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: accentColor, color: "white" }}
            >
              <i className="ri-book-open-line" />
              Open Bible Reader
            </Link>
          </div>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
              <div className="flex items-center gap-3 flex-1">
                <div className="bg-[#111] border border-[#1e1e1e] rounded-lg px-4 py-2 text-center">
                  <p className="text-white text-lg font-bold">{highlights.length}</p>
                  <p className="text-[#555] text-xs">Highlights</p>
                </div>
                <div className="bg-[#111] border border-[#1e1e1e] rounded-lg px-4 py-2 text-center">
                  <p className="text-white text-lg font-bold">{books.length}</p>
                  <p className="text-[#555] text-xs">Books</p>
                </div>
              </div>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="bg-[#111] border border-[#1e1e1e] text-white text-sm rounded-lg px-3 py-2 cursor-pointer focus:outline-none"
              >
                <option value="all">All Books</option>
                {books.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>

            <div className="space-y-6">
              {Object.entries(grouped).map(([groupKey, verses]) => (
                <div key={groupKey}>
                  <h3 className="text-[#555] text-xs uppercase tracking-widest mb-3 px-1">
                    {groupKey}
                  </h3>
                  <div className="space-y-2">
                    {verses.map((h) => (
                      <div
                        key={h.id}
                        className="group flex items-start gap-3 bg-[#111] border border-[#1e1e1e] rounded-xl p-4 hover:border-[#2e2e2e] transition-colors"
                      >
                        <div
                          className="w-1 rounded-full flex-shrink-0 self-stretch min-h-[3rem]"
                          style={{ backgroundColor: h.color }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm leading-relaxed mb-1">{h.verseText}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs" style={{ color: h.color }}>
                              {h.bookName} {h.chapter}:{h.verseNum}
                            </span>
                            <span className="text-[#333] text-xs">{h.translation}</span>
                            <span className="text-[#333] text-xs">
                              {new Date(h.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <Link
                            to="/scripture"
                            title="Open in reader"
                            className="w-7 h-7 flex items-center justify-center rounded-md bg-[#1a1a1a] border border-[#2e2e2e] text-[#666] hover:text-white transition-colors cursor-pointer"
                          >
                            <i className="ri-external-link-line text-xs" />
                          </Link>
                          <button
                            onClick={() => handleDelete(h.id)}
                            title="Remove highlight"
                            className="w-7 h-7 flex items-center justify-center rounded-md bg-[#1a1a1a] border border-[#2e2e2e] text-[#666] hover:text-red-400 transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-xs" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
